import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    History,
    Loader2,
    RefreshCw,
    Printer,
    FileDown,
    User,
} from "lucide-react";
import { toast } from "sonner";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortableHead } from "../components/SortableHead";

const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const KIND_OPTIONS = [
    { value: "all", label: "Tutti i tipi" },
    { value: "single_pdf", label: "Singolo · PDF" },
    { value: "single_print", label: "Singolo · Stampa" },
    { value: "a4_pdf", label: "A4 · PDF" },
    { value: "a4_print", label: "A4 · Stampa" },
];

const KIND_BADGE = {
    single_pdf: { color: "bg-blue-100 text-blue-700", icon: FileDown },
    single_print: { color: "bg-violet-100 text-violet-700", icon: Printer },
    a4_pdf: { color: "bg-emerald-100 text-emerald-700", icon: FileDown },
    a4_print: { color: "bg-amber-100 text-amber-800", icon: Printer },
};

export default function PrintHistoryPage() {
    const [events, setEvents] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [kindFilter, setKindFilter] = useState("all");

    const fetchAll = async (kind) => {
        setLoading(true);
        try {
            const params = {};
            if (kind && kind !== "all") params.kind = kind;
            const [evRes, statsRes] = await Promise.all([
                api.get("/print-events", { params }),
                api.get("/print-events/stats"),
            ]);
            setEvents(evRes.data);
            setStats(statsRes.data);
        } catch (e) {
            toast.error("Errore caricamento storico");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll(kindFilter);
    }, [kindFilter]);

    const { sortedItems: eventsSorted, sortKey, sortDir, requestSort } = useSortableTable(events, {
        customAccessors: {
            dipendente: (ev) => ev.employee_label || `count:${ev.count}`,
        },
    });

    return (
        <div data-testid="print-history-page">
            <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                            <History className="h-6 w-6" />
                        </div>
                        <div>
                            <h1 className="font-display text-2xl font-bold text-[#376DB3]">Storico stampe tesserini</h1>
                            <p className="text-sm text-slate-600">Cronologia di ogni esportazione/stampa con data, tipo e operatore.</p>
                        </div>
                    </div>
                    <Button variant="outline" onClick={() => fetchAll(kindFilter)} disabled={loading} className="gap-2" data-testid="refresh-history-btn">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        Aggiorna
                    </Button>
                </div>

                {/* Stats summary */}
                <div className={`mb-6 grid grid-cols-2 gap-4 md:grid-cols-4 ${stats ? "" : "hidden"}`}>
                    <StatCard label="Operazioni totali" value={stats?.total_events?.toLocaleString("it-IT") || "0"} testId="stat-total-events" />
                    <StatCard label="Tesserini stampati" value={stats?.total_badges_printed?.toLocaleString("it-IT") || "0"} testId="stat-total-badges" />
                    {(stats?.by_kind || []).slice(0, 2).map((k) => (
                        <StatCard
                            key={k.kind}
                            label={k.kind_label}
                            value={k.badges.toLocaleString("it-IT")}
                            sub={`${k.events} operazioni`}
                            testId={`stat-${k.kind}`}
                        />
                    ))}
                </div>

                {/* Filter */}
                <div className="mb-4 flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-600">Filtra per tipo:</span>
                    <Select value={kindFilter} onValueChange={setKindFilter}>
                        <SelectTrigger className="h-9 w-[220px]" data-testid="kind-filter-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {KIND_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Events table */}
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50 hover:bg-slate-50">
                                <SortableHead sortKey="event_at" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
                                    Data &amp; ora
                                </SortableHead>
                                <SortableHead sortKey="kind_label" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
                                    Tipo
                                </SortableHead>
                                <SortableHead sortKey="dipendente" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
                                    Dipendente / Quantità
                                </SortableHead>
                                <SortableHead sortKey="admin_email" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
                                    Operatore
                                </SortableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && eventsSorted.length === 0 ? (
                                <TableRow><TableCell colSpan={4} className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></TableCell></TableRow>
                            ) : eventsSorted.length === 0 ? (
                                <TableRow><TableCell colSpan={4} className="py-16 text-center">
                                    <div className="mx-auto max-w-sm">
                                        <History className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                                        <div className="font-display text-base font-semibold text-slate-700">Nessuna stampa registrata</div>
                                        <p className="mt-1 text-sm text-slate-500">Le stampe e gli export futuri verranno tracciati qui automaticamente.</p>
                                    </div>
                                </TableCell></TableRow>
                            ) : (
                                eventsSorted.map((ev) => {
                                    const meta = KIND_BADGE[ev.kind] || { color: "bg-slate-100 text-slate-700", icon: FileDown };
                                    const Icon = meta.icon;
                                    return (
                                        <TableRow key={ev.id} data-testid={`event-row-${ev.id}`}>
                                            <TableCell className="whitespace-nowrap text-sm text-slate-700 font-mono-numbers">
                                                {fmtDateTime(ev.event_at)}
                                            </TableCell>
                                            <TableCell>
                                                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${meta.color}`}>
                                                    <Icon className="h-3 w-3" />
                                                    {ev.kind_label}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {ev.employee_id ? (
                                                    <Link to={`/dipendenti/${ev.employee_id}`} className="flex items-center gap-2 hover:text-[#376DB3]">
                                                        <User className="h-3.5 w-3.5 text-slate-400" />
                                                        <span className="font-medium">{ev.employee_label || "—"}</span>
                                                        {ev.matricola && <Badge variant="secondary" className="font-mono-numbers text-xs">{ev.matricola}</Badge>}
                                                    </Link>
                                                ) : (
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="font-mono-numbers text-lg font-semibold text-[#376DB3]">{ev.count}</span>
                                                        <span className="text-sm text-slate-500">tesserin{ev.count === 1 ? "o" : "i"}</span>
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-xs text-slate-500">{ev.admin_email}</TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>
            </main>
        </div>
    );
}

const StatCard = ({ label, value, sub, testId }) => (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm" data-testid={testId}>
        <div className="truncate text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
        <div className="font-display text-2xl font-bold text-[#376DB3] font-mono-numbers">{value}</div>
        <div className={`text-[11px] text-slate-500 ${sub ? "" : "hidden"}`}>{sub || ""}</div>
    </div>
);
